import React, { useCallback, useMemo, useRef, useState } from 'react';
import Map, { AttributionControl, Layer, MapMouseEvent, MapRef, NavigationControl, Source } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { Map as MapboxMap } from 'mapbox-gl';
import { MAPBOX_TOKEN, MissingTokenPlaceholder } from './mapPrimitives';

/**
 * NightTrainNetworkMap: every night train Jody knows, on one map (backlog #75,
 * first version). The backend has already simplified the timetables into
 * places and stretches (backend/services/nightTrains/network.js, served by
 * GET /api/public/night-trains/network): a stretch that several trains share
 * is drawn once. Docs: docs/product/night-trains/README.md ("The network map").
 *
 * The journey route map's warm, pale basemap (JourneyRouteMap.tsx), with the
 * big cities' names kept (the network covers a continent, so the reader needs
 * landmarks), straight lines (a network, not one trip) and a conic
 * projection, the usual for maps of Europe. Each stretch is shaded by how
 * many lines use it, in four steps of green (DENSITY_STEPS). Every station
 * where a night train stops has a dot at every zoom; zoomed in on a region,
 * it also has its name. Hovering (or tapping) a stretch or a station says how
 * many lines it has.
 */

export interface NetworkPlace {
  id: number;
  name: string;
  lat: number;
  lng: number;
  /** A night train stops here (false: trains only pass through). */
  stops: boolean;
  /** Lines that stop here. */
  lines: number;
}

export interface NetworkStretch {
  from: number;
  to: number;
  /** Lines that use this stretch, both directions together. */
  lines: number;
}

interface NightTrainNetworkMapProps {
  places: NetworkPlace[];
  stretches: NetworkStretch[];
  /** CSS height of the map, e.g. 600 or 'calc(100vh - 240px)'. */
  height: number | string;
}

const INK = '#16241d';
// The timetables' open-data credits are listed under the Terms and Conditions.
const OPEN_DATA_CREDIT = '<a href="/terms-and-conditions#open-data">Timetables: open data</a>';

// Basemap layers that stay visible, as on the route map (country names and
// borders, land, water, landcover), plus the big cities' names.
const HIDDEN_LINE_OR_FILL = /^(road|bridge|tunnel|aeroway|building|ferry|admin-1|transit|admin-0-boundary-bg)/;
const KEPT_LABELS = new Set(['country-label', 'settlement-major-label']);

// The route map's palette (JourneyRouteMap.tsx), pale so the lines stay the
// most colorful thing on the map.
const BASEMAP = {
  land: '#f6f1e7',
  landcover: '#dde8d3',
  water: '#c4dde9',
  border: '#cbbfa9',
  countryText: '#a89f90',
  cityText: '#7d766a',
};

function styleBasemap(map: MapboxMap) {
  for (const layer of map.getStyle()?.layers ?? []) {
    const { id, type } = layer;
    const hide = type === 'symbol' ? !KEPT_LABELS.has(id) : HIDDEN_LINE_OR_FILL.test(id);
    if (hide) {
      map.setLayoutProperty(id, 'visibility', 'none');
      continue;
    }
    if (type === 'background') {
      map.setPaintProperty(id, 'background-color', BASEMAP.land);
    } else if (type === 'fill' && id.startsWith('water')) {
      map.setPaintProperty(id, 'fill-color', BASEMAP.water);
    } else if (type === 'line' && id.startsWith('waterway')) {
      map.setPaintProperty(id, 'line-color', BASEMAP.water);
    } else if (type === 'fill' && /^(landcover|national-park|landuse)/.test(id)) {
      map.setPaintProperty(id, 'fill-color', BASEMAP.landcover);
    } else if (type === 'line' && id.startsWith('admin-0-boundary')) {
      map.setPaintProperty(id, 'line-color', BASEMAP.border);
    } else if (id === 'country-label') {
      map.setPaintProperty(id, 'text-color', BASEMAP.countryText);
      map.setPaintProperty(id, 'text-halo-color', BASEMAP.land);
    } else if (id === 'settlement-major-label') {
      map.setPaintProperty(id, 'text-color', BASEMAP.cityText);
      map.setPaintProperty(id, 'text-halo-color', BASEMAP.land);
      map.setPaintProperty(id, 'icon-opacity', 0);
    }
  }
}

// Density (Paulo's second version, 2026-10-01): how many lines use a stretch,
// in four steps of one green, pale to deep. Steps rather than a continuous
// scale, so the legend can name them; the busier steps are also wider, so the
// scale reads without color. The ramp passes the dataviz skill's ordinal
// checks against the cream land (one hue, lightness in order, the palest
// step at 2.16:1); the route map's neon green would not (1.3:1, it leans on
// the white casing). 338, 287, 105 and 24 stretches per step with the
// 2026-09-27 package.
const DENSITY_STEPS = [
  { min: 1, color: '#47bb79', width: 1.3, label: '1' },
  { min: 2, color: '#1d9a5c', width: 1.8, label: '2–3' },
  { min: 4, color: '#0f7444', width: 2.5, label: '4–7' },
  { min: 8, color: '#0a4a2d', width: 3.3, label: '8+' },
];
const byDensity = (pick: (step: (typeof DENSITY_STEPS)[number]) => string | number) => [
  'step',
  ['get', 'lines'],
  pick(DENSITY_STEPS[0]),
  ...DENSITY_STEPS.slice(1).flatMap((step) => [step.min, pick(step)]),
];
const DENSITY_COLOR = byDensity((step) => step.color);
// Widths at zoom 3, growing with the zoom: hairlines over the whole
// continent, clear lines once zoomed in on a region.
const DENSITY_WIDTH = byDensity((step) => step.width);
const LINE_WIDTH = ['interpolate', ['linear'], ['zoom'], 3, DENSITY_WIDTH, 6, ['*', 1.8, DENSITY_WIDTH], 9, ['*', 2.6, DENSITY_WIDTH]];
const CASING_WIDTH = [
  'interpolate', ['linear'], ['zoom'],
  3, ['+', DENSITY_WIDTH, 1.6],
  6, ['+', ['*', 1.8, DENSITY_WIDTH], 2.4],
  9, ['+', ['*', 2.6, DENSITY_WIDTH], 3],
];
// Busier stretches are drawn on top of quieter ones.
const BUSY_ON_TOP = ['get', 'lines'];
// Every station where a night train stops has its dot at every zoom (Paulo,
// 2026-10-01: the ends of the lines were missing when only hubs showed).
const DOT_RADIUS = ['interpolate', ['linear'], ['zoom'], 3, 1.6, 6, 2.8, 9, 4.5] as const;
const LABELS_ZOOM = 5.5;
// Room for the legend (top left) and the zoom buttons (top right).
const FRAME_PADDING = { top: 64, bottom: 24, left: 24, right: 24 };

/** The four density steps in one row, then the station dot. */
const Legend: React.FC = () => (
  <div
    style={{
      position: 'absolute',
      top: 10,
      left: 10,
      padding: '6px 9px',
      borderRadius: 8,
      background: 'rgba(255,255,255,0.92)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
      fontSize: 11,
      lineHeight: 1.2,
      color: INK,
    }}
  >
    <div style={{ fontWeight: 700, marginBottom: 4 }}>Night train lines between two stations</div>
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 10, rowGap: 3 }}>
      {DENSITY_STEPS.map((step) => (
        <span key={step.min} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <svg width={20} height={10} aria-hidden="true">
            <line x1={3} y1={5} x2={17} y2={5} stroke="#fff" strokeWidth={step.width * 1.8 + 2.4} strokeLinecap="round" />
            <line x1={3} y1={5} x2={17} y2={5} stroke={step.color} strokeWidth={step.width * 1.8} strokeLinecap="round" />
          </svg>
          {step.label}
        </span>
      ))}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <svg width={10} height={10} aria-hidden="true">
          <circle cx={5} cy={5} r={3} fill="#fff" stroke={INK} strokeWidth={1.2} />
        </svg>
        Station
      </span>
    </div>
  </div>
);

const plural = (n: number) => `${n} night train ${n === 1 ? 'line' : 'lines'}`;

/** What hovering (or tapping) a stretch or a station says. */
interface Hover {
  x: number;
  y: number;
  /** The map's width, to keep the box inside it. */
  width: number;
  title: string;
  detail: string;
}

const NightTrainNetworkMap: React.FC<NightTrainNetworkMapProps> = ({ places, stretches, height }) => {
  const mapRef = useRef<MapRef | null>(null);

  // NB: the imported `Map` (react-map-gl) shadows the global Map constructor
  // in this file, so places are looked up through a plain object.
  const { lineGeoJson, stopGeoJson, bounds } = useMemo(() => {
    const byId: Record<number, NetworkPlace> = {};
    for (const p of places) byId[p.id] = p;

    const lineFeatures = stretches
      .filter((s) => byId[s.from] && byId[s.to])
      .map((s) => ({
        type: 'Feature' as const,
        properties: { lines: s.lines, from: byId[s.from].name, to: byId[s.to].name },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [byId[s.from].lng, byId[s.from].lat],
            [byId[s.to].lng, byId[s.to].lat],
          ],
        },
      }));

    const stopFeatures = places
      .filter((p) => p.stops)
      .map((p) => ({
        type: 'Feature' as const,
        properties: { name: p.name, lines: p.lines },
        geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
      }));

    let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
    for (const p of places) {
      if (p.lng < minLng) minLng = p.lng;
      if (p.lng > maxLng) maxLng = p.lng;
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
    }

    return {
      lineGeoJson: { type: 'FeatureCollection' as const, features: lineFeatures },
      stopGeoJson: { type: 'FeatureCollection' as const, features: stopFeatures },
      bounds: places.length
        ? ([[minLng, minLat], [maxLng, maxLat]] as [[number, number], [number, number]])
        : null,
    };
  }, [places, stretches]);

  // Restyle as soon as the style is parsed, before the first tiles (see
  // JourneyRouteMap: waiting for 'load' flashes the raw style first).
  const attachMap = useCallback((ref: MapRef | null) => {
    mapRef.current = ref;
    if (!ref) return;
    const map = ref.getMap();
    if (map.isStyleLoaded()) styleBasemap(map);
    else map.once('style.load', () => styleBasemap(map));
  }, []);

  // The whole network at first sight: nobody should miss the Arctic line or
  // Sicily. fitBounds is only approximate in a conic projection (it cut the
  // lines south of Paris on a phone), so the frame is then corrected with the
  // places' real positions on screen, twice since the projection bends.
  const frameNetwork = useCallback(() => {
    const ref = mapRef.current;
    if (!bounds || !ref) return;
    const map = ref.getMap();
    map.fitBounds(bounds, { padding: FRAME_PADDING, duration: 0 });
    const { clientWidth: w, clientHeight: h } = map.getContainer();
    const pad = FRAME_PADDING;
    for (let i = 0; i < 2; i++) {
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      for (const p of places) {
        const { x, y } = map.project([p.lng, p.lat]);
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
      if (!(maxX > minX && maxY > minY)) return;
      const scale = Math.min((w - pad.left - pad.right) / (maxX - minX), (h - pad.top - pad.bottom) / (maxY - minY));
      // A map with no size yet (a hidden tab) gives a negative scale, and a
      // NaN zoom crashes Mapbox: keep fitBounds' frame then.
      if (!(scale > 0) || !Number.isFinite(scale)) return;
      // Put the network's middle in the middle of the padded frame.
      const frameX = (pad.left + w - pad.right) / 2;
      const frameY = (pad.top + h - pad.bottom) / 2;
      const center = map.unproject([
        (minX + maxX) / 2 + (w / 2 - frameX) / scale,
        (minY + maxY) / 2 + (h / 2 - frameY) / scale,
      ]);
      if (!Number.isFinite(center.lng) || !Number.isFinite(center.lat)) return;
      const zoom = Math.min(map.getMaxZoom(), Math.max(map.getMinZoom(), map.getZoom() + Math.log2(scale)));
      map.jumpTo({ center, zoom });
    }
  }, [bounds, places]);

  // A station wins over the stretches under it (it is listed first).
  const [hover, setHover] = useState<Hover | null>(null);
  const showHover = useCallback((e: MapMouseEvent) => {
    const f = e.features?.[0];
    if (!f) {
      setHover(null);
      return;
    }
    const lines = Number(f.properties?.lines) || 0;
    const width = e.target.getContainer().clientWidth;
    setHover(
      f.layer?.id === 'night-train-stops-dot'
        ? { x: e.point.x, y: e.point.y, width, title: String(f.properties?.name ?? ''), detail: `${plural(lines)} stop here` }
        : { x: e.point.x, y: e.point.y, width, title: `${f.properties?.from} to ${f.properties?.to}`, detail: plural(lines) }
    );
  }, []);
  const hideHover = useCallback(() => setHover(null), []);

  if (!MAPBOX_TOKEN) return <MissingTokenPlaceholder height={typeof height === 'number' ? height : 480} />;

  return (
    <div
      style={{
        position: 'relative',
        height,
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
      }}
    >
      <Map
        ref={attachMap}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{ longitude: 12, latitude: 50, zoom: 3.5 }}
        mapStyle="mapbox://styles/mapbox/light-v11"
        // The usual projection for maps of Europe, not Mercator (or the globe
        // Mapbox shows zoomed out): Mercator blows up Scandinavia, and the
        // network runs from Sicily to Narvik, so the core would be tiny.
        projection={{ name: 'lambertConformalConic', center: [15, 50], parallels: [40, 60] }}
        style={{ width: '100%', height: '100%' }}
        minZoom={2.5}
        maxZoom={11}
        attributionControl={false}
        dragRotate={false}
        touchPitch={false}
        onLoad={frameNetwork}
        interactiveLayerIds={['night-train-stops-dot', 'night-train-stretches-hit']}
        onMouseMove={showHover}
        onMouseLeave={hideHover}
        onClick={showHover}
        cursor={hover ? 'pointer' : 'grab'}
      >
        <NavigationControl position="top-right" showCompass={false} />
        {/* The open-data licences' notice, where the data is shown and as discreet as Mapbox's own (Paulo, 2026-10-01). */}
        <AttributionControl customAttribution={OPEN_DATA_CREDIT} />
        <Source id="night-train-stretches" type="geojson" data={lineGeoJson}>
          <Layer
            id="night-train-stretches-casing"
            type="line"
            layout={{ 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': BUSY_ON_TOP as any }}
            paint={{ 'line-color': '#ffffff', 'line-width': CASING_WIDTH as any, 'line-opacity': 0.9 }}
          />
          <Layer
            id="night-train-stretches-line"
            type="line"
            layout={{ 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': BUSY_ON_TOP as any }}
            paint={{ 'line-color': DENSITY_COLOR as any, 'line-width': LINE_WIDTH as any }}
          />
          {/* Invisible and wider than the lines: easier to hover or tap. */}
          <Layer
            id="night-train-stretches-hit"
            type="line"
            layout={{ 'line-sort-key': BUSY_ON_TOP as any }}
            paint={{ 'line-color': '#000000', 'line-opacity': 0.01, 'line-width': 12 }}
          />
        </Source>
        <Source id="night-train-stops" type="geojson" data={stopGeoJson}>
          <Layer
            id="night-train-stops-dot"
            type="circle"
            paint={{
              'circle-radius': DOT_RADIUS as any,
              'circle-color': '#ffffff',
              'circle-stroke-color': INK,
              'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 3, 0.8, 8, 1.4] as any,
            }}
          />
          {/* Station names once zoomed in on a region; where they collide, the busiest station keeps its name. */}
          <Layer
            id="night-train-stops-name"
            type="symbol"
            minzoom={LABELS_ZOOM}
            layout={{
              'text-field': ['get', 'name'],
              'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
              'text-size': 11,
              'text-anchor': 'top',
              'text-offset': [0, 0.5],
              'text-optional': true,
              'symbol-sort-key': ['-', ['get', 'lines']],
            }}
            paint={{ 'text-color': INK, 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 }}
          />
        </Source>
      </Map>
      <Legend />
      {hover && (
        <div
          style={{
            position: 'absolute',
            // Right of the pointer, or left of it near the map's right edge.
            left: hover.x > hover.width - 240 ? undefined : hover.x + 14,
            right: hover.x > hover.width - 240 ? hover.width - hover.x + 14 : undefined,
            top: hover.y + 14,
            maxWidth: 240,
            padding: '6px 8px',
            borderRadius: 6,
            background: '#111827',
            color: '#ffffff',
            fontSize: 12,
            lineHeight: 1.35,
            pointerEvents: 'none',
            zIndex: 3,
          }}
        >
          <div style={{ fontWeight: 600 }}>{hover.title}</div>
          <div style={{ opacity: 0.85 }}>{hover.detail}</div>
        </div>
      )}
    </div>
  );
};

export default NightTrainNetworkMap;
